import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './ChatBot.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://chatbotapi.kaireai.com';

const processTextWithLinks = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  const parts = text.split(/(KaireAI)/g);
  return parts.map((part, index) => {
    if (part === 'KaireAI') {
      return (
        <a 
          key={index}
          href="https://kaireai.com/subscription-plans"
          target="_blank"
          rel="noopener noreferrer"
          className="kaireai-link"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const ChatBot = ({ onNewMessage, onSessionChange, onConnectionStatusChange }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechRecognition, setSpeechRecognition] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [sessionInfo, setSessionInfo] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const [lastActiveTime, setLastActiveTime] = useState(Date.now());
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [hasCheckedSignupReturn, setHasCheckedSignupReturn] = useState(false);

  const SESSION_STORAGE_KEY = 'kaireai_session_data';

  // Save session to storage
  const saveSessionToStorage = useCallback((sessionId, messages, sessionInfo) => {
    try {
      const sessionData = {
        sessionId,
        messages,
        sessionInfo,
        timestamp: Date.now(),
        version: '1.0'
      };
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
    } catch (error) {
      console.warn('Failed to save session:', error);
    }
  }, []);

  // Load session from storage
  const loadSessionFromStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(SESSION_STORAGE_KEY);
      if (saved) {
        const sessionData = JSON.parse(saved);
        const maxAge = 24 * 60 * 60 * 1000;
        if (Date.now() - sessionData.timestamp < maxAge) {
          console.log('Loaded session from localStorage:', sessionData.sessionId);
          return sessionData;
        } else {
          console.log('Stored session expired, creating new session');
          localStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.warn('Failed to load session from localStorage:', error);
    }
    return null;
  }, []);

  // Create new session
  const createNewSession = useCallback(() => {
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return newSessionId;
  }, []);

  // Add message
  const addMessage = useCallback((message) => {
    setMessages(prev => {
      const updated = [...prev, message];
      if (sessionId) {
        saveSessionToStorage(sessionId, updated, sessionInfo);
      }
      return updated;
    });
  }, [sessionId, sessionInfo, saveSessionToStorage]);

  // Track signup attempt on server
  const trackSignupAttempt = useCallback(async () => {
    if (!sessionId) return;
    
    try {
      await axios.post(`${API_URL}/api/session/${sessionId}/signup-attempt`);
      console.log('✅ Signup attempt tracked on server');
    } catch (error) {
      console.warn('Failed to track signup attempt:', error);
    }
  }, [sessionId]);

  // Track signup completion on server
  const trackSignupCompletion = useCallback(async () => {
    if (!sessionId) return;
    
    try {
     await axios.post(`${API_URL}/api/session/${sessionId}/signup-complete`);
      console.log('🎉 Signup completion tracked on server');
    } catch (error) {
      console.warn('Failed to track signup completion:', error);
    }
  }, [sessionId]);

// Track care advisor attempt on server
const trackCareAdvisorAttempt = useCallback(async () => {
  if (!sessionId) return;
  
  try {
   await axios.post(`${API_URL}/api/session/${sessionId}/care-advisor-attempt`);
    console.log('✅ Care advisor attempt tracked on server');
  } catch (error) {
    console.warn('Failed to track care advisor attempt:', error);
  }
}, [sessionId]);

// Track care advisor completion on server
const trackCareAdvisorCompletion = useCallback(async () => {
  if (!sessionId) return;
  
  try {
   await axios.post(`${API_URL}/api/session/${sessionId}/care-advisor-complete`);
    console.log('🎉 Care advisor completion tracked on server');
  } catch (error) {
    console.warn('Failed to track care advisor completion:', error);
  }
}, [sessionId]);

// Check signup status and show welcome back message ONLY ONCE
const checkSignupReturn = useCallback(async () => {
  if (hasCheckedSignupReturn || !sessionId) {
    console.log('❌ Skipping signup return check:', { hasCheckedSignupReturn, sessionId });
    return;
  }
  
  const signupAttempted = sessionStorage.getItem('kaireai_signup_attempted');
  const signupTime = sessionStorage.getItem('kaireai_signup_time');
  
  console.log('🔍 Checking signup return:', { signupAttempted, signupTime });
  
  if (signupAttempted === 'true' && signupTime) {
    const timeSinceSignup = Date.now() - parseInt(signupTime);
    const tenMinutes = 10 * 60 * 1000;
    
    if (timeSinceSignup < tenMinutes) {
      console.log('✅ Showing welcome back message');
      
      // IMPORTANT: Set this IMMEDIATELY to prevent duplicate messages
      setHasCheckedSignupReturn(true);
      
      // Clear flags IMMEDIATELY
      sessionStorage.removeItem('kaireai_signup_attempted');
      sessionStorage.removeItem('kaireai_signup_time');
      sessionStorage.removeItem('kaireai_signup_url');
      
      // Show simple welcome back message
      const welcomeBackMessage = {
        id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'bot',
        content: `Welcome back! 👋\n\nDid you complete your signup?`,
        buttons: [
          {
            text: '✅ Yes, I Signed Up!',
            value: 'yes i completed signup',
            type: 'completion'
          },
          {
            text: '❌ Not Yet',
            value: 'not yet still thinking',
            type: 'default'
          },
          {
            text: '💡 Tell Me More',
            value: 'kaireai features',
            type: 'default'
          },
          {
            text: '🏠 Main Menu',
            value: 'main menu',
            type: 'default'
          }
        ],
        timestamp: Date.now(),
        status: 'delivered'
      };
      
      addMessage(welcomeBackMessage);
    } else {
      console.log('⏰ Signup attempt too old, clearing flags');
      // Too old, just clear the flags
      sessionStorage.removeItem('kaireai_signup_attempted');
      sessionStorage.removeItem('kaireai_signup_time');
      sessionStorage.removeItem('kaireai_signup_url');
      setHasCheckedSignupReturn(true);
    }
  } else {
    console.log('ℹ️ No signup attempt found');
    setHasCheckedSignupReturn(true);
  }
}, [sessionId, hasCheckedSignupReturn, addMessage]);

// Check care advisor status and show welcome back message ONLY ONCE
const checkCareAdvisorReturn = useCallback(async () => {
  if (hasCheckedSignupReturn || !sessionId) {
    console.log('❌ Skipping care advisor return check:', { hasCheckedSignupReturn, sessionId });
    return;
  }
  
  const careAdvisorAttempted = sessionStorage.getItem('kaireai_care_advisor_attempted');
  const careAdvisorTime = sessionStorage.getItem('kaireai_care_advisor_time');
  
  console.log('🔍 Checking care advisor return:', { careAdvisorAttempted, careAdvisorTime });
  
  if (careAdvisorAttempted === 'true' && careAdvisorTime) {
    const timeSinceCareAdvisor = Date.now() - parseInt(careAdvisorTime);
    const tenMinutes = 10 * 60 * 1000;
    
    if (timeSinceCareAdvisor < tenMinutes) {
      console.log('✅ Showing care advisor welcome back message');
      
      // IMPORTANT: Set this IMMEDIATELY to prevent duplicate messages
      setHasCheckedSignupReturn(true);
      
      // Clear flags IMMEDIATELY
      sessionStorage.removeItem('kaireai_care_advisor_attempted');
      sessionStorage.removeItem('kaireai_care_advisor_time');
      sessionStorage.removeItem('kaireai_care_advisor_url');
      
      // Show care advisor welcome back message
      const welcomeBackMessage = {
        id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'bot',
        content: `Welcome back! 👋\n\nDid you get to speak with our care advisor team?`,
        buttons: [
          {
            text: '✅ Yes, I Spoke to Them!',
            value: 'yes i spoke to care advisor',
            type: 'care_advisor_completion'
          },
          {
            text: '❌ Not Yet',
            value: 'not yet still waiting',
            type: 'default'
          },
          {
            text: '💡 Tell Me More',
            value: 'kaireai features',
            type: 'default'
          },
          {
            text: '🏠 Main Menu',
            value: 'main menu',
            type: 'default'
          }
        ],
        timestamp: Date.now(),
        status: 'delivered'
      };
      
      addMessage(welcomeBackMessage);
    } else {
      console.log('⏰ Care advisor attempt too old, clearing flags');
      // Too old, just clear the flags
      sessionStorage.removeItem('kaireai_care_advisor_attempted');
      sessionStorage.removeItem('kaireai_care_advisor_time');
      sessionStorage.removeItem('kaireai_care_advisor_url');
      setHasCheckedSignupReturn(true);
    }
  } else {
    console.log('ℹ️ No care advisor attempt found');
    setHasCheckedSignupReturn(true);
  }
}, [sessionId, hasCheckedSignupReturn, addMessage]);

  // Initialize session and speech recognition
  useEffect(() => {
    const initializeSession = () => {
      const savedSession = loadSessionFromStorage();
      
      if (savedSession && savedSession.messages && savedSession.messages.length > 0) {
        setSessionId(savedSession.sessionId);
        setMessages(savedSession.messages);
        setSessionInfo(savedSession.sessionInfo);
        setIsInitialized(true);
        setShowWelcome(false);
        
        axios.post(`https://chatbotapi.kaireai.com/api/session/${savedSession.sessionId}/restore`, {
          sessionData: savedSession.sessionInfo
        }).catch(error => {
          console.warn('Failed to restore session on server:', error);
        });
      } else {
        const newSessionId = createNewSession();
        setSessionId(newSessionId);
        setShowWelcome(true);
      }
    };

    const initializeSpeechRecognition = () => {
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onstart = () => setIsListening(true);
        
        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          const cleanedTranscript = cleanupSpeechText(transcript);
          setInputValue(cleanedTranscript);
          setIsListening(false);

          setTimeout(() => {
            const form = document.querySelector('.input-form');
            if (form) {
              const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
              form.dispatchEvent(submitEvent);
            }
          }, 100);
        };
        
        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
        };
        recognition.onend = () => setIsListening(false);

        setSpeechRecognition(recognition);
      }
    };

    initializeSession();
    initializeSpeechRecognition();
  }, [loadSessionFromStorage, createNewSession]);

 // Check for signup return after initialization - ONLY ONCE
// Check for signup OR care advisor return after initialization - ONLY ONCE
useEffect(() => {
  if (sessionId && isInitialized && !showWelcome && !hasCheckedSignupReturn) {
    console.log('🎯 Initializing return check');
    
    // Check if care advisor was attempted (higher priority)
    const careAdvisorAttempted = sessionStorage.getItem('kaireai_care_advisor_attempted');
    const signupAttempted = sessionStorage.getItem('kaireai_signup_attempted');
    
    setTimeout(() => {
      if (careAdvisorAttempted === 'true') {
        console.log('🔄 Care advisor attempted, checking care advisor return');
        checkCareAdvisorReturn();
      } else if (signupAttempted === 'true') {
        console.log('🔄 Signup attempted, checking signup return');
        checkSignupReturn();
      }
    }, 500);
  }
}, [sessionId, isInitialized, showWelcome, hasCheckedSignupReturn, checkSignupReturn, checkCareAdvisorReturn]);


// Prevent welcome back message from showing after any interaction
// Prevent welcome back message from showing after any interaction
useEffect(() => {
  if (messages.length > 1 && !hasCheckedSignupReturn) {
    // If user has sent any messages, don't show welcome back
    const userMessages = messages.filter(m => m.type === 'user');
    if (userMessages.length > 0) {
      console.log('🚫 User already interacting, disabling welcome back check');
      setHasCheckedSignupReturn(true);
      // Clear ALL flags
      sessionStorage.removeItem('kaireai_signup_attempted');
      sessionStorage.removeItem('kaireai_signup_time');
      sessionStorage.removeItem('kaireai_signup_url');
      sessionStorage.removeItem('kaireai_care_advisor_attempted');
      sessionStorage.removeItem('kaireai_care_advisor_time');
      sessionStorage.removeItem('kaireai_care_advisor_url');
    }
  }
}, [messages, hasCheckedSignupReturn]);


  // Save session whenever messages or sessionInfo change
  useEffect(() => {
    if (sessionId && isInitialized) {
      saveSessionToStorage(sessionId, messages, sessionInfo);
      if (onSessionChange) {
        onSessionChange({ sessionId, messages, sessionInfo });
      }
    }
  }, [sessionId, messages, sessionInfo, isInitialized, saveSessionToStorage, onSessionChange]);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Auto-focus input after bot messages
  useEffect(() => {
    if (!showWelcome && messages.length > 0 && !isLoading) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === 'bot') {
        setTimeout(() => {
          if (inputRef.current && !isListening) {
            inputRef.current.focus();
          }
        }, 500);
      }
    }
  }, [messages, showWelcome, isLoading, isListening]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'end'
    });
  };

  const startListening = () => {
    if (speechRecognition && !isListening) {
      speechRecognition.start();
      setTimeout(() => {
        if (isListening && speechRecognition) {
          speechRecognition.stop();
        }
      }, 8000);
    }
  };

  const stopListening = () => {
    if (speechRecognition && isListening) {
      speechRecognition.stop();
    }
  };

  const cleanupSpeechText = (text) => {
    let cleanedText = text;

    cleanedText = cleanedText.replace(/\bone\b/gi, '1');
    cleanedText = cleanedText.replace(/\btwo\b/gi, '2');
    cleanedText = cleanedText.replace(/\bthree\b/gi, '3');
    cleanedText = cleanedText.replace(/\bfour\b/gi, '4');
    cleanedText = cleanedText.replace(/\bfive\b/gi, '5');
    cleanedText = cleanedText.replace(/\bsix\b/gi, '6');
    cleanedText = cleanedText.replace(/\bseven\b/gi, '7');
    cleanedText = cleanedText.replace(/\beight\b/gi, '8');
    cleanedText = cleanedText.replace(/\bnine\b/gi, '9');
    cleanedText = cleanedText.replace(/\bzero\b/gi, '0');
    cleanedText = cleanedText.replace(/\boh\b/gi, '0');

    cleanedText = cleanedText.replace(/\b([a-zA-Z]+)\s+([0-9\s]+)\s*@\s*([a-zA-Z]+)\.com\b/gi, (match, name, numbers, domain) => {
      return name + numbers.replace(/\s+/g, '') + '@' + domain + '.com';
    });

    cleanedText = cleanedText.replace(/\b([a-zA-Z0-9\s]+)\s+at\s+the\s+rate\s+of\s+([a-zA-Z]+)\.com\b/gi, (match, username, domain) => {
      return username.replace(/\s+/g, '') + '@' + domain + '.com';
    });

    cleanedText = cleanedText.replace(/\b([a-zA-Z0-9\s]+)\s+at\s+([a-zA-Z]+)\.com\b/gi, (match, username, domain) => {
      return username.replace(/\s+/g, '') + '@' + domain + '.com';
    });

    cleanedText = cleanedText.replace(/([a-zA-Z]+)\s+([0-9]+)\s*@/gi, '$1$2@');
    
    cleanedText = cleanedText.replace(/\s+at\s+the\s+rate\s+of\s+/gi, '@');
    cleanedText = cleanedText.replace(/\s+at\s+rate\s+of\s+/gi, '@');
    cleanedText = cleanedText.replace(/\s+@\s+/gi, '@');

  // Common domain fixes

    cleanedText = cleanedText.replace(/gmail\.com/gi, 'gmail.com');
    cleanedText = cleanedText.replace(/yahoo\.com/gi, 'yahoo.com');
    cleanedText = cleanedText.replace(/hotmail\.com/gi, 'hotmail.com');
    cleanedText = cleanedText.replace(/outlook\.com/gi, 'outlook.com');

    cleanedText = cleanedText.replace(/\bplus\b/gi, '+');
    cleanedText = cleanedText.replace(/\+\s+(\d)/gi, '+$1');
    cleanedText = cleanedText.replace(/(\+?\d[\d\s]{8,})/g, (match) => {
      return match.replace(/\s+/g, '');
    });
    
    cleanedText = cleanedText.replace(/\bdash\b/gi, '-');
    cleanedText = cleanedText.replace(/\bhyphen\b/gi, '-');
    cleanedText = cleanedText.replace(/\s+/g, ' ').trim();

    return cleanedText;
  };

  const updateConnectionStatus = useCallback((status) => {
    setConnectionStatus(status);
    if (onConnectionStatusChange) {
      onConnectionStatusChange(status);
    }
  }, [onConnectionStatusChange]);

  const startConversation = (message = 'hello') => {
    setShowWelcome(false);
    sendMessage(message, null, true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 600);
  };

const sendMessage = async (message, customSessionId = null, isInitial = false, retryCount = 0) => {
  const currentSessionId = customSessionId || sessionId;
  
  if (!message.trim() && !isInitial) return;
  if (isLoading && !isInitial) return;

  // IMPORTANT: Check care advisor FIRST, then signup
  const lowerMessage = message.trim().toLowerCase();

  // NEW: Intercept typed "care advisor" and redirect (CHECK THIS FIRST!)
if (!isInitial && (
  lowerMessage.includes('talk to care advisor') ||
  lowerMessage.includes('talk to a care advisor') ||
  lowerMessage.includes('care advisor') ||
  lowerMessage.includes('speak to care') ||
  lowerMessage.includes('talk to specialist') ||
  lowerMessage.includes('contact care') ||
  lowerMessage.includes('talk to agent') ||
  lowerMessage.includes('speak to advisor') ||
  lowerMessage.match(/^(I want a real person|customer care|agent please|talk to support|I want to talk to a person|can I speak to someone|connect me with a human|get human help|talk to care team|connect me with care advisor)$/i)
)) 
{    console.log('🔄 Intercepting care advisor typed message, redirecting to care advisor page');
    
     const phoneNumber = '+18665247324';
  
  await trackCareAdvisorAttempt();
  
  sessionStorage.setItem('kaireai_care_advisor_attempted', 'true');
  sessionStorage.setItem('kaireai_care_advisor_time', Date.now().toString());
  sessionStorage.setItem('kaireai_care_advisor_phone', phoneNumber);
  
  // Add user message
  const userMessage = {
    id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'user',
    content: message,
    timestamp: Date.now(),
    status: 'delivered'
  };
  addMessage(userMessage);
  setInputValue('');
  
  // Open phone dialer
  window.location.href = `tel:${phoneNumber}`;
  
  // Show confirmation message
  const confirmationMessage = {
    id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'bot',
    content: `Perfect! Opening dialer with our care advisor number: ${phoneNumber} 📞\n\nOur care advisors can help you with:\n\n✅ Personalized health plan setup\n✅ Understanding your health data\n✅ Device setup and troubleshooting\n✅ Family dashboard configuration\n✅ Emergency response planning\n✅ Medication management guidance\n\nThey typically respond within a few minutes. Feel free to continue exploring!`,
    buttons: [
      {
        text: '✅ I Spoke to Care Advisor',
        value: 'I spoke to care advisor',
        type: 'care_advisor_completion'
      },
      {
        text: '📞 Call Again',
        value: 'call care advisor again',
        type: 'care_advisor'
      },
      {
        text: '🏠 Main Menu',
        value: 'main menu',
        type: 'navigation'
      }
    ]
  };
  addMessage(confirmationMessage);
  
  return;
}

  // Intercept typed "signup" and redirect (CHECK THIS SECOND)
if (!isInitial && (
  lowerMessage.includes('sign up') ||
  lowerMessage.includes('signup') ||
  lowerMessage.includes('register') ||
  lowerMessage.includes('join kaireai') ||
  lowerMessage.includes('create account') ||
  lowerMessage.includes('get started') ||
  lowerMessage.includes('enroll') ||
  lowerMessage.includes('subscribe') ||
  lowerMessage.match(/^(I want to register|I want to join|I want to sign up|I am ready to join|where is the signup|show me signup|how can I sign up|how do I register|sign me up|lets join)$/i)
)) 
{
      console.log('🔄 Intercepting signup typed message, redirecting to signup page');
    
     const signupUrl = 'https://kaireai.com/subscription-plans';
  
  await trackSignupAttempt();
  
  // Add user message FIRST
  const userMessage = {
    id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'user',
    content: message,
    timestamp: Date.now(),
    status: 'delivered'
  };
  addMessage(userMessage);
  setInputValue('');
  
  // Show confirmation message
  const confirmationMessage = {
    id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'bot',
    content: `Perfect! I've opened the signup page. 🎉\n\nHere's what you'll get with KaireAI:\n\n✅ 24/7 Health Monitoring with AI\n✅ Emergency Fall Detection & Alerts\n✅ Smart Medication Reminders\n✅ Family Dashboard for Peace of Mind\n✅ Personalized Health Insights\n✅ Secure Telemedicine Access\n\nThe signup process takes just 3-5 minutes. I'll stay right here while you complete it!`,
    buttons: [
      {
        text: '✅ I Completed Signup',
        value: 'I completed signup successfully',
        type: 'completion'
      },
      {
        text: '💡 Tell Me More',
        value: 'kaireai features',
        type: 'default'
      },
      {
        text: '🏠 Main Menu',
        value: 'main menu',
        type: 'default'
      }
    ],
    timestamp: Date.now(),
    status: 'delivered'
  };
  addMessage(confirmationMessage);
  
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'signup_typed', {
      'event_category': 'conversion',
      'event_label': 'typed_signup_intercepted'
    });
  }
  
  if (onNewMessage) {
    onNewMessage();
  }
  
  // Save to localStorage and navigate after delay
  setTimeout(() => {
    saveSessionToStorage(sessionId, [...messages, userMessage, confirmationMessage], sessionInfo);
    window.location.href = signupUrl;
  }, 300);
  
  return;
}

  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
    abortControllerRef.current = new AbortController();

    if (showWelcome && !isInitial) {
      setShowWelcome(false);
    }

    setIsLoading(true);
    updateConnectionStatus('sending');
    setLastActiveTime(Date.now());

    let userMessage = null;

    if (!isInitial) {
      userMessage = {
        id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'user',
        content: message,
        timestamp: Date.now(),
        status: 'sending'
      };
      addMessage(userMessage);
      setInputValue('');
    }

    try {
     const response = await axios.post(`${API_URL}/api/chat`, {
        message: message || 'hello',
        sessionId: currentSessionId,
        context: {
          isInitial: isInitial,
          previousMessages: messages.slice(-5),
          timestamp: Date.now(),
          connectionStatus: connectionStatus,
          userAgent: navigator.userAgent,
          forceFlowReset: message && (message.toLowerCase().includes('bye') || message.toLowerCase().includes('goodbye'))
        }
      }, {
        timeout: 30000,
        signal: abortControllerRef.current.signal
      });

      await new Promise(resolve => setTimeout(resolve, Math.min(500, response.data.text?.length * 10 || 500)));

      updateConnectionStatus('connected');
      setReconnectAttempts(0);

      const botMessage = {
        id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'bot',
        content: response.data.text || 'I understand. How can I help you today?',
        buttons: response.data.buttons || [],
        cards: response.data.cards || [],
        images: response.data.images || [],
        lists: response.data.lists || [],
        carousels: response.data.carousels || [],
        quickReplies: response.data.quickReplies || [],
        timestamp: Date.now(),
        status: 'delivered',
        metadata: {
          intent: response.data.intent,
          confidence: response.data.confidence,
          currentPage: response.data.currentPage,
          currentFlow: response.data.currentFlow,
          responseTime: Date.now() - (userMessage?.timestamp || Date.now())
        }
      };

      addMessage(botMessage);

      setTimeout(() => {
        if (inputRef.current && !isListening) {
          inputRef.current.focus();
        }
      }, 300);
      
      if (response.data.sessionInfo) {
        setSessionInfo(response.data.sessionInfo);
      }

      setIsInitialized(true);

      if (onNewMessage && !isInitial) {
        onNewMessage();
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request was cancelled');
        return;
      }

      console.error('Error sending message:', error);
      updateConnectionStatus('error');
      
      if (retryCount < 3 && !['ECONNABORTED', 'ERR_CANCELED'].includes(error.code)) {
        console.log(`Retrying message send (attempt ${retryCount + 1}/3)...`);
        setReconnectAttempts(prev => prev + 1);
        
        retryTimeoutRef.current = setTimeout(() => {
          sendMessage(message, customSessionId, isInitial, retryCount + 1);
        }, Math.pow(2, retryCount) * 1000);
        return;
      }
      
      const errorMessage = {
        id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'bot',
        content: getErrorMessage(error, retryCount),
        buttons: [{ 
          text: 'Retry', 
          value: message || 'hello',
          type: 'quick_reply'
        }],
        timestamp: Date.now(),
        status: 'error',
        metadata: {
          isError: true,
          errorType: error.code || 'unknown',
          retryCount: retryCount
        }
      };

      addMessage(errorMessage);
      setIsInitialized(true);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      
      setTimeout(() => {
        if (inputRef.current && !isListening) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  // Save chat state to localStorage
const saveChatState = useCallback(() => {
  const chatState = {
    messages: messages,
    sessionId: sessionId,
    timestamp: Date.now()
  };
  localStorage.setItem('kaireai_chat_state', JSON.stringify(chatState));
  console.log('💾 Chat state saved to localStorage');
}, [messages, sessionId]);

// Load chat state from localStorage on mount
useEffect(() => {
  const savedState = localStorage.getItem('kaireai_chat_state');
  if (savedState) {
    try {
      const parsed = JSON.parse(savedState);
      // Only restore if less than 30 minutes old
      if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
        console.log('📂 Restoring chat state from localStorage');
        setMessages(parsed.messages || []);
        setSessionId(parsed.sessionId);
      } else {
        localStorage.removeItem('kaireai_chat_state');
      }
    } catch (error) {
      console.error('Failed to restore chat state:', error);
    }
  }
}, []); // Run only once on mount

  const getErrorMessage = (error, retryCount) => {
    if (error.response?.status === 429) {
      return "I'm receiving too many requests right now. Please wait a moment and try again.";
    } else if (error.response?.status >= 500) {
      return "I'm experiencing some technical difficulties. Please try again.";
    } else if (error.code === 'ECONNABORTED') {
      return "The request timed out. Please check your connection and try again.";
    } else if (retryCount >= 2) {
      return "I'm having trouble connecting. Please check your internet connection or try refreshing the page.";
    } else {
      return "I'm having trouble processing your request. Let me try again.";
    }
  };
    
  useEffect(() => {
    if (sessionId) {
      updateConnectionStatus('connected');
    }
  }, [sessionId, updateConnectionStatus]);

  useEffect(() => {
    const startHeartbeat = () => {
      heartbeatIntervalRef.current = setInterval(async () => {
        if (Date.now() - lastActiveTime > 60000) {
          try {
            await axios.get('https://chatbotapi.kaireai.com/api/health', { timeout: 5000 });
            updateConnectionStatus('connected');
          } catch (error) {
            updateConnectionStatus('error');
          }
        }
      }, 30000);
    };

    if (sessionId && isInitialized) {
      startHeartbeat();
    }

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [sessionId, isInitialized, lastActiveTime, updateConnectionStatus]);

  // ENHANCED: Handle button click with signup logic
// ENHANCED: Handle button click with signup logic
const handleButtonClick = async (button) => {
  console.log('Button clicked:', button);
  
  // Enhanced signup detection - BUT exclude completion buttons
  const isCompletionButton = 
    button.type === 'completion' ||
    button.text.toLowerCase().includes('completed signup') ||
    button.text.toLowerCase().includes('signed up') ||
    button.value?.toLowerCase().includes('completed signup') ||
    button.value?.toLowerCase().includes('signed up');
  
  // FIX 1: Handle signup completion with better message
  if (isCompletionButton) {
    console.log('✅ Signup completion button clicked');
    
    await trackSignupCompletion();
    
    const celebrationMessage = {
      id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'bot',
      content: `🎉 Fantastic! Welcome to the KaireAI family!\n\nYou've just taken a huge step towards better health management. You now have access to:\n\n✅ 24/7 AI-Powered Health Monitoring\n✅ Real-Time Emergency Detection\n✅ Smart Medication Management\n✅ Family Care Dashboard\n✅ Personalized Health Insights\n\nWhat would you like to explore first?`,
      buttons: [
        {
          text: '📱 Download Mobile App',
          value: 'download app',
          type: 'app_download'
        },
        {
          text: '💡 Explore All Features',
          value: 'kaireai features',
          type: 'default'
        },
        {
          text: '🔬 See Smart Sensors in Action',
          value: 'smart sensors',
          type: 'default'
        },
        {
          text: '🏠 Main Menu',
          value: 'main menu',
          type: 'default'
        }
      ],
      timestamp: Date.now(),
      status: 'delivered'
    };
    
    addMessage(celebrationMessage);
    
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'signup_completed', {
        'event_category': 'conversion',
        'event_label': 'signup_success',
        'value': 1
      });
    }
    
    return;
  }
  
  // Handle care advisor completion
  const isCareAdvisorCompletion = 
    button.type === 'care_advisor_completion' ||
    button.value?.toLowerCase().includes('spoke to care advisor');

  if (isCareAdvisorCompletion) {
    console.log('✅ Care advisor completion button clicked');
    
    await trackCareAdvisorCompletion();
    
    const completionMessage = {
      id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'bot',
      content: `Wonderful! I'm glad our care advisor could help you. 🎉\n\nOur team is always here to support your health journey. Here's what you can do next:\n\n📱 Download the mobile app for 24/7 access\n💡 Explore all KaireAI features\n🔬 See our smart sensors in action\n📊 Set up your health dashboard\n\nWhat would you like to do?`,
      buttons: [
        {
          text: '📱 Download Mobile App',
          value: 'download app',
          type: 'app_download'
        },
        {
          text: '💡 Explore Features',
          value: 'kaireai features',
          type: 'default'
        },
        {
          text: '🔬 See Smart Sensors',
          value: 'smart sensors',
          type: 'default'
        },
        {
          text: '🏠 Main Menu',
          value: 'main menu',
          type: 'default'
        }
      ],
      timestamp: Date.now(),
      status: 'delivered'
    };
    
    addMessage(completionMessage);
    
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'care_advisor_completed', {
        'event_category': 'engagement',
        'event_label': 'care_advisor_success'
      });
    }
    
    return;
  }

  // IMPORTANT: Check CARE ADVISOR buttons BEFORE signup buttons!
  const isCareAdvisorButton = 
    button.type === 'care_advisor' ||
    button.action === 'redirect_care' ||
    button.text.toLowerCase().includes('care advisor') ||
    button.text.toLowerCase().includes('talk to care') ||
    button.text.toLowerCase().includes('speak to care') ||
    button.value?.toLowerCase().includes('care advisor') ||
    button.value?.toLowerCase().includes('talk to care');

 if (isCareAdvisorButton) {
  console.log('👨‍⚕️ Care advisor button clicked');
  
  const phoneNumber = '+18665247324'; // The number from your requirement
  
  await trackCareAdvisorAttempt();
  
  // Store flags
  sessionStorage.setItem('kaireai_care_advisor_attempted', 'true');
  sessionStorage.setItem('kaireai_care_advisor_time', Date.now().toString());
  sessionStorage.setItem('kaireai_care_advisor_phone', phoneNumber);
  
  // Open phone dialer using tel: protocol
  window.location.href = `tel:${phoneNumber}`;
  
  // Show confirmation message
  const confirmationMessage = {
    id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'bot',
    content: `Perfect! I've opened the dialer with our care advisor number: ${phoneNumber} 📞\n\nOur care advisors can help you with:\n\n✅ Personalized health plan setup\n✅ Understanding your health data\n✅ Device setup and troubleshooting\n✅ Family dashboard configuration\n✅ Emergency response planning\n✅ Medication management guidance\n\nFeel free to continue exploring while you wait!`,
    buttons: [
        {
          text: '✅ I Spoke to Care Advisor',
          value: 'I spoke to care advisor',
          type: 'care_advisor_completion'
        },
        {
          text: '🔗 Open Care Advisor Again',
          value: phoneNumber,
          type: 'url'
        },
        {
          text: '💡 Explore Features',
          value: 'kaireai features',
          type: 'default'
        },
        {
          text: '🏠 Main Menu',
          value: 'main menu',
          type: 'default'
        }
      ],
      timestamp: Date.now(),
      status: 'delivered'
    };
    addMessage(confirmationMessage);
    
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'care_advisor_clicked', {
        'event_category': 'engagement',
        'event_label': button.text,
        'value': 1
      });
    }
    
    if (onNewMessage) {
      onNewMessage();
    }
    
    return; // IMPORTANT: Return here to prevent further checks
  }

  // Check signup buttons (AFTER care advisor check)
  const isSignupButton = 
    !isCompletionButton && (
      button.type === 'signup' ||
      button.action === 'redirect' ||
      button.priority === true ||
      (button.text.toLowerCase().includes('signup') && !button.text.toLowerCase().includes('completed')) || 
      (button.text.toLowerCase().includes('sign up') && !button.text.toLowerCase().includes('completed')) ||
      button.text.toLowerCase().includes('register') ||
      button.text.toLowerCase().includes('get started')
    );
  
 if (isSignupButton) {
  console.log('📝 Signup button clicked');
  
  const signupUrl = button.url || 'https://kaireai.com/subscription-plans';
  
  await trackSignupAttempt();
  
  // Show confirmation message FIRST
  const confirmationMessage = {
    id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'bot',
    content: `Perfect! I've opened the signup page. 🎉\n\nHere's what you'll get with KaireAI:\n\n✅ 24/7 Health Monitoring with AI\n✅ Emergency Fall Detection & Alerts\n✅ Smart Medication Reminders\n✅ Family Dashboard for Peace of Mind\n✅ Personalized Health Insights\n✅ Secure Telemedicine Access\n\nThe signup process takes just 3-5 minutes. I'll stay right here while you complete it!`,
    buttons: [
      {
        text: '✅ I Completed Signup',
        value: 'I completed signup successfully',
        type: 'completion'
      },
      {
        text: '💡 Tell Me More',
        value: 'kaireai features',
        type: 'default'
      },
      {
        text: '🏠 Main Menu',
        value: 'main menu',
        type: 'default'
      }
    ],
    timestamp: Date.now(),
    status: 'delivered'
  };
  
  // Add message to state
  addMessage(confirmationMessage);
  
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'signup_button_clicked', {
      'event_category': 'conversion',
      'event_label': button.text,
      'value': 1
    });
  }
  
  if (onNewMessage) {
    onNewMessage();
  }
  
  // Save to localStorage and navigate after a short delay
  setTimeout(() => {
    saveSessionToStorage(sessionId, [...messages, confirmationMessage], sessionInfo);
    window.location.href = signupUrl;
  }, 300);
  
  return;
}

  // Handle app download
 // Handle app download
if (button.type === 'app_download' || 
    button.value?.toLowerCase().includes('download app') ||
    button.text.toLowerCase().includes('download app')) {
  
  console.log('📱 App download button clicked');
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const platform = navigator.platform || '';
  
  // IMPROVED: Better Android detection
  const isAndroid = /android/i.test(userAgent);
  
  // IMPROVED: Better iOS detection
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) || 
                (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Additional checks
  const isWindowsMobile = /windows phone/i.test(userAgent);
  const isDesktop = !isAndroid && !isIOS && !isWindowsMobile && 
                    /windows|mac|linux/i.test(userAgent.toLowerCase() + ' ' + platform.toLowerCase());
  
  console.log('Device detection:', { 
    isAndroid,
    isIOS,
    isWindowsMobile,
    isDesktop,
    platform,
    userAgent: userAgent.substring(0, 100)
  });
  
  if (isAndroid && !isDesktop) {
    // Real Android device - redirect to Play Store
    console.log('✅ Detected: Real Android device - redirecting to Play Store');
    
    const androidUrl = 'https://play.google.com/store/apps/details?id=com.kaire.iot';
    window.open(androidUrl, '_blank', 'noopener,noreferrer');
    
    const androidMessage = {
      id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'bot',
      content: `📱 Great! I've opened the Google Play Store for you.\n\nDownload the KaireAI app to get started with:\n\n✅ Real-time health monitoring\n✅ Instant emergency alerts\n✅ Smart medication reminders\n✅ Family dashboard access\n\nOnce installed, log in with your signup credentials!`,
      buttons: [
        {
          text: '🔗 Open Play Store Again',
          value: androidUrl,
          type: 'url'
        },
        {
          text: '💡 Learn About Features',
          value: 'kaireai features',
          type: 'default'
        },
        {
          text: '🏠 Main Menu',
          value: 'main menu',
          type: 'default'
        }
      ],
      timestamp: Date.now(),
      status: 'delivered'
    };
    
    addMessage(androidMessage);
    
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'app_download_android', {
        'event_category': 'engagement',
        'event_label': 'play_store_redirect'
      });
    }
    
  } else if (isIOS && !isDesktop) {
    // Real iOS device - redirect to App Store
    console.log('✅ Detected: Real iOS device - redirecting to App Store');
    
    const iosUrl = 'https://apps.apple.com/us/app/kaireai-smart-care-at-home/id6746397249';
    window.open(iosUrl, '_blank', 'noopener,noreferrer');
    
    const iosMessage = {
      id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'bot',
      content: `📱 Perfect! I've opened the Apple App Store for you.\n\nDownload the KaireAI app to get started with:\n\n✅ Real-time health monitoring\n✅ Instant emergency alerts\n✅ Smart medication reminders\n✅ Family dashboard access\n\nOnce installed, log in with your signup credentials!`,
      buttons: [
        {
          text: '🔗 Open App Store Again',
          value: iosUrl,
          type: 'url'
        },
        {
          text: '💡 Learn About Features',
          value: 'kaireai features',
          type: 'default'
        },
        {
          text: '🏠 Main Menu',
          value: 'main menu',
          type: 'default'
        }
      ],
      timestamp: Date.now(),
      status: 'delivered'
    };
    
    addMessage(iosMessage);
    
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'app_download_ios', {
        'event_category': 'engagement',
        'event_label': 'app_store_redirect'
      });
    }
    
  } else {
    // Desktop/Web OR unknown device - show both links
    console.log('✅ Detected: Desktop or unknown device - showing both options');
    
    const webMessage = {
      id: `bot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'bot',
      content: `📱 Great choice! Download the KaireAI app for the best mobile experience.\n\nThe mobile app gives you:\n\n✅ Real-time health monitoring on the go\n✅ Instant emergency push alerts\n✅ Smart medication reminders\n✅ Quick access to family dashboard\n\nChoose your platform:`,
      buttons: [
        {
          text: '🤖 Android App',
          value: 'https://play.google.com/store/apps/details?id=com.kaire.iot',
          type: 'url'
        },
        {
          text: '🍎 iOS App',
          value: 'https://apps.apple.com/us/app/kaireai-smart-care-at-home/id6746397249',
          type: 'url'
        },
        {
          text: '💡 Learn More',
          value: 'kaireai features',
          type: 'default'
        },
        {
          text: '🏠 Main Menu',
          value: 'main menu',
          type: 'default'
        }
      ],
      timestamp: Date.now(),
      status: 'delivered'
    };
    
    addMessage(webMessage);
    
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'app_download_web', {
        'event_category': 'engagement',
        'event_label': 'both_links_shown'
      });
    }
  }
  
  return;
}
  
  // Handle regular URL buttons
  if (button.type === 'url' && button.value?.startsWith('http')) {
    console.log('🔗 URL button clicked');
    window.open(button.value, '_blank', 'noopener,noreferrer');
    return;
  }
  
  // Handle all other buttons - just send to Dialogflow
  console.log('💬 Regular button clicked, sending to Dialogflow:', button.value || button.text);
  sendMessage(button.value || button.text);
};

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      sendMessage(inputValue);
    }
  };

const clearChat = () => {
  setMessages([]);
  setSessionInfo(null);
  setIsInitialized(false);
  setShowWelcome(true);
  setHasCheckedSignupReturn(false); // Reset this flag
  
  localStorage.removeItem(SESSION_STORAGE_KEY);
  // Clear ALL session storage flags
  sessionStorage.removeItem('kaireai_signup_attempted');
  sessionStorage.removeItem('kaireai_signup_time');
  sessionStorage.removeItem('kaireai_signup_url');
  sessionStorage.removeItem('kaireai_care_advisor_attempted');
  sessionStorage.removeItem('kaireai_care_advisor_time');
  sessionStorage.removeItem('kaireai_care_advisor_url');
  
  if (sessionId) {
    axios.delete(`https://chatbotapi.kaireai.com/api/session/${sessionId}`)
      .catch(error => console.warn('Failed to delete session on server:', error));
  }
  
  const newSessionId = createNewSession();
  setSessionId(newSessionId);
};

  const welcomeButtons = [
    { text: '🏥 KaireAI Features', value: 'KaireAI Features' },
    { text: '👨‍⚕️ KaireAI for Caregivers', value: 'caregivers' },
    { text: '👴 KaireAI for Elders/Seniors', value: 'Seniors' },
    { text: '👋 Just Say Hi', value: 'hello' }
  ];

  return (
    <div className="chatbot-container">
      <div className={`connection-status ${connectionStatus}`}>
        <div className="status-indicator"></div>
        <span className="status-text">
          {connectionStatus === 'connected' ? 'Connected' :
           connectionStatus === 'sending' ? 'Sending...' :
           connectionStatus === 'error' ? 'Connection Error' :
           'Connecting...'}
        </span>
      </div>

      <div className="messages-container">
        {showWelcome ? (
          <div className="welcome-message">
            <div className="bot-avatar-large">
              <img src="https://res.cloudinary.com/daccflyf2/image/upload/v1757413277/kaireai-nurse_pjjnpu.jpg" 
                   alt="KaireAI Assistant" />
            </div>
           
            <h3>Hi, I'm Kaire</h3>
            <p>I'm your intelligent healthcare assistant. I can help you with health monitoring, appointments, emergency assistance, and much more.</p>
            
            <div className="welcome-buttons">
              {welcomeButtons.map((button, index) => (
                <button
                  key={index}
                  className="welcome-button"
                  onClick={() => startConversation(button.value)}
                >
                  {button.text}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageComponent
                key={message.id}
                message={message}
                onButtonClick={handleButtonClick}
                showDebug={process.env.NODE_ENV === 'development'}
              />
            ))}
            
            {isLoading && (
              <div className="message bot">
                <div className="message-wrapper">
                  <div className="message-avatar">
                    <div className="bot-avatar">
                      <img src="https://res.cloudinary.com/daccflyf2/image/upload/v1757413277/kaireai-nurse_pjjnpu.jpg" 
                           alt="KaireAI Assistant" />
                    </div>
                  </div>
                  <div className="message-bubble">
                    <div className="message-content">
                      <div className="typing-indicator">
                        <div className="typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <span className="typing-text">KaireAI is thinking...</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="chat-footer">
        {!showWelcome && messages.length > 0 && (
          <div className="chat-actions">
            <button className="clear-chat-btn" onClick={clearChat} title="Start fresh conversation">
              <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="btn-text">New Chat</span>
            </button>
          </div>
        )}
        
        {!showWelcome && (
          <form onSubmit={handleSubmit} className="input-form">
            <div className="input-container">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message or click the microphone to speak..."
                disabled={isLoading}
                className="message-input"
              />
              
              {speechRecognition && (
                <button
                  type="button"
                  onClick={isListening ? stopListening : startListening}
                  disabled={isLoading}
                  className={`voice-button ${isListening ? 'listening' : ''}`}
                  title={isListening ? 'Stop listening' : 'Start voice input'}
                >
                  {isListening ? '🔴' : '🎤'}
                </button>
              )}
              
              <button 
                type="submit" 
                disabled={isLoading || !inputValue.trim()} 
                className="send-button"
                title="Send message"
              >
                <span className="send-icon">➤</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Enhanced Message Component
const MessageComponent = ({ message, onButtonClick, showDebug = false }) => {
  
  return (
    <div className={`message ${message.type}`}>
      <div className="message-wrapper">
        {message.type === 'bot' && (
          <div className="message-avatar">
            <div className="bot-avatar">
              <img src="https://res.cloudinary.com/daccflyf2/image/upload/v1757413277/kaireai-nurse_pjjnpu.jpg" 
                   alt="KaireAI Assistant" />
            </div>
          </div>
        )}
        
        <div className="message-bubble">
          <div className="message-content">
            <div className="message-text">
              {message.content.split('\n').map((line, idx) => (
                <div key={idx} className="message-line">
                  {processTextWithLinks(line)}
                </div>
              ))}
            </div>
          </div>
          
          {message.images && message.images.length > 0 && (
            <div className="message-images">
              {message.images.map((image, idx) => (
                <div key={idx} className="image-container">
                  <img 
                    src={image.url} 
                    alt={image.alt} 
                    className="message-image"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}

          {message.cards && message.cards.length > 0 && (
            <div className="message-cards">
              {message.cards.map((card, idx) => (
                <div key={idx} className="card">
                  {card.imageUri && (
                    <div className="card-image">
                      <img 
                        src={card.imageUri} 
                        alt={card.title} 
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="card-body">
                    {card.title && <h4 className="card-title">{card.title}</h4>}
                    {card.subtitle && <p className="card-subtitle">{card.subtitle}</p>}
                    {card.buttons && card.buttons.length > 0 && (
                      <div className="card-buttons">
                        {card.buttons.map((button, btnIdx) => (
                          <button
                            key={btnIdx}
                            className="card-button"
                            onClick={() => onButtonClick(button)}
                          >
                            {button.text}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {message.carousels && message.carousels.length > 0 && (
            <div className="message-carousels">
              {message.carousels.map((carousel, idx) => (
                <div key={idx} className="carousel">
                  <div className="carousel-items">
                    {carousel.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="carousel-item">
                        {item.imageUri && (
                          <img src={item.imageUri} alt={item.title} />
                        )}
                        <div className="carousel-content">
                          <h4>{item.title}</h4>
                          <p>{item.subtitle}</p>
                          {item.buttons && (
                            <div className="carousel-buttons">
                              {item.buttons.map((button, btnIdx) => (
                                <button
                                  key={btnIdx}
                                  onClick={() => onButtonClick(button)}
                                >
                                  {button.text}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {message.lists && message.lists.length > 0 && (
            <div className="message-lists">
              {message.lists.map((list, idx) => (
                <div key={idx} className="list">
                  {list.title && <h4 className="list-title">{list.title}</h4>}
                  <div className="list-items">
                    {list.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="list-item">
                        {item.image && (
                          <img src={item.image} alt={item.title} />
                        )}
                        <div className="list-item-content">
                          <h5>{item.title}</h5>
                          <p>{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {(message.buttons && message.buttons.length > 0) || 
           (message.quickReplies && message.quickReplies.length > 0) ? (
            <div className={`message-buttons ${(message.buttons?.length || message.quickReplies?.length || 0) > 4 ? 'many-buttons' : ''}`}>
              {(message.buttons || message.quickReplies || []).map((button, btnIndex) => {
                const isSignupButton = 
                  button.type === 'signup' ||
                  button.action === 'redirect' ||
                  button.priority === true ||
                  button.text.toLowerCase().includes('signup') || 
                  button.text.toLowerCase().includes('sign up') ||
                  button.text.toLowerCase().includes('register') ||
                  button.text.toLowerCase().includes('get started') ||
                  button.value?.toLowerCase().includes('signup');
                
                return (
                  <button
                    key={btnIndex}
                    className={`chat-button ${button.type || 'default'} ${isSignupButton ? 'priority-signup' : ''}`}
                    onClick={() => onButtonClick(button)}
                  >
                    <span className="button-text">{button.text}</span>
                    {isSignupButton && <span className="priority-indicator">✨</span>}
                    {button.type === 'url' && !isSignupButton && (
                      <span className="button-icon">🔗</span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ChatBot;